import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  AccountBalanceWallet,
  People,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  getTenantDashboardSummary,
  getTenantSalesTrend,
  getTenantRecentTransactions,
  getTenantPayoutHistory,
  getTenantProducts,
  getTenantUsers,
  TenantDashboardSummary,
  SalesTrendItem,
  TenantTransaction,
  TenantPayout,
  TenantProduct,
  TenantUser,
} from '../../services/api/tenantDashboardService';

// 金額フォーマット
const formatCurrency = (amount: number): string => {
  return `¥${amount.toLocaleString()}`;
};

// 日付フォーマット
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
};

const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// サマリーカード
interface SummaryCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: number;
  color?: string;
}

const SummaryCard = ({ title, value, subtitle, icon, trend, color = '#1976d2' }: SummaryCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          {trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {trend >= 0 ? (
                <TrendingUp sx={{ color: 'success.main', fontSize: 20, mr: 0.5 }} />
              ) : (
                <TrendingDown sx={{ color: 'error.main', fontSize: 20, mr: 0.5 }} />
              )}
              <Typography
                variant="body2"
                sx={{ color: trend >= 0 ? 'success.main' : 'error.main' }}
              >
                {trend >= 0 ? '+' : ''}{trend}% 前月比
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}15`,
            p: 1.5,
            borderRadius: 2,
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// タブパネル
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function TenantDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const [summary, setSummary] = useState<TenantDashboardSummary | null>(null);
  const [salesTrend, setSalesTrend] = useState<SalesTrendItem[]>([]);
  const [transactions, setTransactions] = useState<TenantTransaction[]>([]);
  const [payouts, setPayouts] = useState<{ data: TenantPayout[]; summary: { total_paid: number; total_pending: number } }>({
    data: [],
    summary: { total_paid: 0, total_pending: 0 },
  });
  const [products, setProducts] = useState<TenantProduct[]>([]);
  const [users, setUsers] = useState<{ total: number; data: TenantUser[] }>({ total: 0, data: [] });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryData, trendData, txData, payoutData, productData, userData] = await Promise.all([
          getTenantDashboardSummary(),
          getTenantSalesTrend(6),
          getTenantRecentTransactions(5),
          getTenantPayoutHistory(10),
          getTenantProducts(),
          getTenantUsers(0, 10),
        ]);

        setSummary(summaryData);
        setSalesTrend(trendData);
        setTransactions(txData);
        setPayouts(payoutData);
        setProducts(productData);
        setUsers(userData);
      } catch (err) {
        setError('データの取得に失敗しました。テナントに所属していない可能性があります。');
        console.error('Tenant dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // プロダクト売上の最大値
  const maxProductSales = Math.max(...products.map((p) => p.total_sales), 1);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        マイダッシュボード
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        あなたのテナントの売上・収益状況
      </Typography>

      {/* サマリーカード */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="今月の売上"
            value={formatCurrency(summary?.this_month_sales || 0)}
            trend={summary?.growth_rate}
            icon={<ShoppingCart sx={{ fontSize: 32 }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="累計収益"
            value={formatCurrency(summary?.total_earnings || 0)}
            subtitle={`今月: ${formatCurrency(summary?.this_month_earnings || 0)}`}
            icon={<AccountBalanceWallet sx={{ fontSize: 32 }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="未払い収益"
            value={formatCurrency(summary?.pending_earnings || 0)}
            subtitle="次回支払い予定"
            icon={<AccountBalanceWallet sx={{ fontSize: 32 }} />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="アクティブユーザー"
            value={`${summary?.active_users || 0}名`}
            subtitle={`取引: ${summary?.this_month_transactions || 0}件（今月）`}
            icon={<People sx={{ fontSize: 32 }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* タブ */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="概要" />
          <Tab label="取引履歴" />
          <Tab label="支払い履歴" />
          <Tab label="プロダクト" />
          <Tab label="ユーザー" />
        </Tabs>
      </Card>

      {/* 概要タブ */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* 月別売上推移 */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  月別売上推移
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={salesTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={(v) => `¥${(v / 10000).toFixed(0)}万`} />
                      <Tooltip
                        formatter={(value: number) => [formatCurrency(value), '売上']}
                      />
                      <Bar dataKey="sales" fill="#1976d2" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* プロダクト別売上 */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  プロダクト別売上
                </Typography>
                {products.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    データがありません
                  </Typography>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    {products.map((product) => (
                      <Box key={product.id} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {product.custom_name || product.product_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCurrency(product.total_sales)}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(product.total_sales / maxProductSales) * 100}
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* 取引履歴タブ */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              最近の取引
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>日時</TableCell>
                    <TableCell>プロダクト</TableCell>
                    <TableCell>顧客</TableCell>
                    <TableCell>プラン</TableCell>
                    <TableCell align="right">金額</TableCell>
                    <TableCell>ステータス</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        取引データがありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(tx.transaction_date)}
                        </TableCell>
                        <TableCell>{tx.product_name || '-'}</TableCell>
                        <TableCell>
                          {tx.customer_name || tx.customer_email || '-'}
                        </TableCell>
                        <TableCell>{tx.plan_name || '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                          {formatCurrency(tx.gross_amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={tx.status}
                            size="small"
                            color={tx.status === 'completed' ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* 支払い履歴タブ */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  支払いサマリー
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography color="text.secondary">支払い済み</Typography>
                    <Typography fontWeight="bold" color="success.main">
                      {formatCurrency(payouts.summary.total_paid)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">支払い待ち</Typography>
                    <Typography fontWeight="bold" color="warning.main">
                      {formatCurrency(payouts.summary.total_pending)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  支払い履歴
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>対象期間</TableCell>
                        <TableCell align="right">金額</TableCell>
                        <TableCell>ステータス</TableCell>
                        <TableCell>支払日</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payouts.data.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            支払い履歴がありません
                          </TableCell>
                        </TableRow>
                      ) : (
                        payouts.data.map((payout) => (
                          <TableRow key={payout.id}>
                            <TableCell>
                              {formatDate(payout.period_start)} - {formatDate(payout.period_end)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                              {formatCurrency(payout.net_amount)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={payout.status === 'completed' ? '支払済' : '処理中'}
                                size="small"
                                color={payout.status === 'completed' ? 'success' : 'warning'}
                              />
                            </TableCell>
                            <TableCell>
                              {payout.paid_date ? formatDate(payout.paid_date) : '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* プロダクトタブ */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              販売中プロダクト
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>プロダクト名</TableCell>
                    <TableCell>コード</TableCell>
                    <TableCell align="right">累計売上</TableCell>
                    <TableCell align="right">今月売上</TableCell>
                    <TableCell align="right">取引数</TableCell>
                    <TableCell>ステータス</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        プロダクトがありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {product.custom_name || product.product_name}
                          </Typography>
                          {product.custom_name && (
                            <Typography variant="caption" color="text.secondary">
                              ({product.product_name})
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{product.product_code}</TableCell>
                        <TableCell align="right">{formatCurrency(product.total_sales)}</TableCell>
                        <TableCell align="right">{formatCurrency(product.this_month_sales)}</TableCell>
                        <TableCell align="right">{product.transaction_count}件</TableCell>
                        <TableCell>
                          <Chip
                            label={product.is_enabled ? '有効' : '無効'}
                            size="small"
                            color={product.is_enabled ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* ユーザータブ */}
      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                ユーザー一覧
              </Typography>
              <Chip label={`${users.total}名`} color="primary" size="small" />
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>メールアドレス</TableCell>
                    <TableCell>氏名</TableCell>
                    <TableCell>役割</TableCell>
                    <TableCell>ライセンス期限</TableCell>
                    <TableCell>ステータス</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        ユーザーがいません
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.data.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.full_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.role === 'admin' ? '管理者' : 'ユーザー'}
                            size="small"
                            color={user.role === 'admin' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {user.license_valid_until ? formatDate(user.license_valid_until) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'アクティブ' : '無効'}
                            size="small"
                            color={user.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
}
